// -----------------------------------------------------------------------------
// AttendedGuidedChecklistV2  (Phase 4a — v2 animation variant)
//
// Drop-in replacement for AttendedGuidedChecklist that renders v2 sub-components
// (UpNextCardV2, ChecklistListV2) with richer directional entrance animations:
//
//   Stats strip  — slide-down from -12px (fadeSx → translateY(-12px) → none)
//   UpNext card  — slide-up from +24px (handled inside UpNextCardV2)
//   List rows    — slide from left with stagger (handled inside ChecklistListV2)
//
// All v1 gate / spawn / retry / preview logic is faithfully preserved.
// data-testid="attended-guided-checklist-v2" on the outer wrapper so tests can
// distinguish v1 from v2 renders.
//
// Activate by visiting any attended popup with ?guidedV=2 in the URL.
// The switcher lives in AttendedAppointment.jsx and
// ConsultationAttendedAppointment.jsx — this file is pure v2.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography, Button, useMediaQuery, Dialog, DialogContent, DialogActions } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { dispatch } from 'src/redux/store';
import { completeChecklistItem, reopenChecklistItem } from 'src/redux/slices/taskSlice';
import { CRM_PRIMARY_BUTTON_SX } from 'src/styles/crmButtons';
import { CHECKLIST_COMPLETION_STATE } from 'src/constants/tasksV2';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';
import cascadeTaskDueDates from 'src/utils/cascadeTaskDueDates';
import { FollowUp } from '../../../../../components/tabs';
import useGuidedChecklist from '../hooks/useGuidedChecklist';
import useUndoAttendance from '../hooks/useUndoAttendance';
import AttendanceGate from '../AttendanceGate';
import UndoBanner from '../UndoBanner';
import UpNextCardV2 from './UpNextCardV2';
import RecentlyCompletedCard from '../RecentlyCompletedCard';
import PostSendExpandedCard from './PostSendExpandedCard';
import ChecklistMiniGrid from '../ChecklistMiniGrid';
import ActionPanel from '../ActionPanel';
import BulkAssignModal from '../BulkAssignModal';
import { INLINE_HANDLED_TYPES } from '../panels';
import FollowUpPanel from '../panels/FollowUpPanel';
import { COMPLETION_LABELS, DEFAULT_COMPLETION_LABEL } from '../actionMeta';

const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';
const BOOKED_STATUS = 'CONSULTATION_BOOKED';
const SPAWN_INITIAL_DELAY_MS = 600;
const SPAWN_RETRY_DELAY_MS = 1000;
const SPAWN_MAX_RETRIES = 2;

// ---------------------------------------------------------------------------

// Spawn-failure / no-checklist fallback. There's no checklist task for this
// lead, so (1) let the user RECORD what they actually did to contact the patient
// — reusing the proven FollowUpPanel channel picker (Call · WhatsApp · Email ·
// Note); the sub-panels log the note/call to /leads/{id}/notes/ on their own, so
// a null `item` is fine — and (2) keep the manual follow-up/reminder below.
// onTaskActivity fires on mount + on any recorded action so the host's outcome
// gate (activeTab) isn't left empty and trapping Finish ("Please select any one
// Tab"). We do NOT complete a checklist item (there is none) — onComplete just
// records host activity.
function SpawnFailureFallback({
    handleClose,
    onTaskActivity,
    practiceId,
    leadId,
    leadDetail,
    onOpenSidebarTab,
    handleReloadLeads,
}) {
    useEffect(() => {
        if (onTaskActivity) onTaskActivity();
    }, [onTaskActivity]);
    const handleRecorded = () => {
        // Contact already logged by the sub-panel; just mark host activity.
        // Don't close — the follow-up scheduler stays available below.
        if (onTaskActivity) onTaskActivity();
    };
    return (
        <Box data-testid="spawn-failure-fallback" sx={{ py: 1 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1d1d1f', mb: 0.25, letterSpacing: '-0.01em' }}>
                No checklist for this lead
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#6e6e73', mb: 1, letterSpacing: '-0.005em' }}>
                Record what you did to contact the patient, then set a follow-up below.
            </Typography>
            <FollowUpPanel
                item={null}
                practiceId={practiceId}
                leadId={leadId}
                leadDetail={leadDetail}
                onClose={handleClose}
                onComplete={handleRecorded}
                handleReloadLeads={handleReloadLeads}
                onOpenSidebarTab={onOpenSidebarTab}
            />
            <Box sx={{ borderTop: '1px solid rgba(145,158,171,0.16)', mt: 1.5, pt: 1.5 }}>
                <FollowUp type="lead" handleClose={handleClose} />
            </Box>
        </Box>
    );
}
SpawnFailureFallback.propTypes = {
    handleClose: PropTypes.func,
    onTaskActivity: PropTypes.func,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    onOpenSidebarTab: PropTypes.func,
    handleReloadLeads: PropTypes.func,
};

export default function AttendedGuidedChecklistV2({
    practiceId,
    leadId,
    leadDetail,
    mode,
    handleClose,
    handleNoAttended,
    gateRenderedInHost,
    externalGateDismissed,
    handleReloadLeads,
    onTaskActivity,
    onOpenSidebarTab,
}) {
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    // Skip the gate if the lead is already CONSULTATION_ATTENDED (handles re-open).
    const initiallyAttended = (leadDetail?.lead_treatments || []).some(
        (t) => t.status === 'CONSULTATION_ATTENDED'
    );
    const [gateDismissed, setGateDismissed] = useState(initiallyAttended);
    // Undo-attendance banner — only active in gate-first mode.
    const { showBanner, secondsLeft, startUndo, undo, dismiss: dismissUndo } = useUndoAttendance();
    const [featuredOverride, setFeaturedOverride] = useState(null);
    const [actionPanelItem, setActionPanelItem] = useState(null);
    const [bodyMounted, setBodyMounted] = useState(false);
    const [spawnLoading, setSpawnLoading] = useState(false);
    const [spawnGaveUp, setSpawnGaveUp] = useState(false);
    const spawnRetryRef = useRef(0);
    // Optimistic due-date overrides: Map<taskId, Date> applied before rendering.
    const [dueDateOverrides, setDueDateOverrides] = useState(() => new Map());
    // Post-action transition state.
    const [recentlyCompleted, setRecentlyCompleted] = useState(null);
    const recentlyCompletedTimerRef = useRef(null);
    const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
    // Pause dialog — tracks which item is being paused and the resume datetime
    const [pauseDialogItem, setPauseDialogItem] = useState(null);
    const [pauseUntilDate, setPauseUntilDate] = useState('');
    // Optimistic completion tracking — prevents stale GET data from reverting the mini-grid
    // while the busted-cache refresh is in-flight or when the BE auto-completes an item
    // asynchronously (e.g. after transactional-message send).
    // Items are added immediately on complete/skip/send; cleared once the server's done
    // group confirms them (reconcile effect below).
    const [optimisticDoneIds, setOptimisticDoneIds] = useState(() => new Set());
    // When the gate is owned internally AND the lead is already CONSULTATION_ATTENDED
    // at mount, skip the spawn-refresh sequence — tasks were created in a prior session.
    // When gateRenderedInHost=true the host drives dismissal, so never skip.
    const skipSpawnRef = useRef(!gateRenderedInHost && initiallyAttended);

    const { loading, initialLoading, templatesEmpty, groups, upNext, templatePreviewItems = [], allDoneCount, refresh } = useGuidedChecklist({
        practiceId,
        leadId,
        leadDetail,
    });

    const { today: rawToday = [], later: rawLater = [], done = [] } = groups || {};
    // Apply optimistic exclusion: items we know are done but the server hasn't confirmed yet.
    // Short-circuit when the set is empty (normal case) to avoid unnecessary filter passes.
    const today = optimisticDoneIds.size > 0 ? rawToday.filter(i => !optimisticDoneIds.has(i.id)) : rawToday;
    const later = optimisticDoneIds.size > 0 ? rawLater.filter(i => !optimisticDoneIds.has(i.id)) : rawLater;
    const totalCount = today.length + later.length + done.length;
    const doneCount = done.length;
    // allDone via two paths:
    //   1. BE returns completed items in checklist_items → normal count check.
    //   2. BE omits completed items (server-side filter) → allDoneCount > 0 from
    //      progress-based detection in the hook.
    const allDone = (totalCount > 0 && doneCount === totalCount) || allDoneCount > 0;
    const effectiveTotalCount = allDoneCount > 0 ? allDoneCount : totalCount;

    const effectiveGateDismissed = gateRenderedInHost ? !!externalGateDismissed : gateDismissed;

    // Use initialLoading (not loading) so that post-action refreshes don't hide the body.
    // Once tasks have loaded once, subsequent loading cycles don't flash the spinner or
    // blank out the card — keeping recentlyCompleted and featuredOverride visible.
    const showBody =
        !initialLoading &&
        !templatesEmpty &&
        !spawnLoading &&
        (mode !== 'gate-first' || effectiveGateDismissed);

    // ---------------------------------------------------------------------------
    // Stagger mount animation trigger
    // ---------------------------------------------------------------------------
    useEffect(() => {
        if (!showBody) {
            setBodyMounted(false);
            return undefined;
        }
        const t = setTimeout(() => setBodyMounted(true), 16);
        return () => clearTimeout(t);
    }, [showBody]);

    // ---------------------------------------------------------------------------
    // Unmount cleanup — clear the recentlyCompleted timer so that if the popup
    // closes within the 4-second ack window the orphaned timeout can't fire
    // onTaskActivity on an already-unmounted parent and trigger a stray tab switch.
    // ---------------------------------------------------------------------------
    useEffect(() => () => clearTimeout(recentlyCompletedTimerRef.current), []);

    // ---------------------------------------------------------------------------
    // Spawn-refresh sequence
    // ---------------------------------------------------------------------------
    useEffect(() => {
        if (!effectiveGateDismissed) {
            spawnRetryRef.current = 0;
            return undefined;
        }
        // Lead was already CONSULTATION_ATTENDED at mount — tasks exist, no spawn needed.
        if (skipSpawnRef.current) return undefined;
        setSpawnLoading(true);
        spawnRetryRef.current = 0;
        const t = setTimeout(() => refresh(), SPAWN_INITIAL_DELAY_MS);
        return () => clearTimeout(t);
    }, [effectiveGateDismissed]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---------------------------------------------------------------------------
    // Retry if tasks still absent after completed refresh
    // ---------------------------------------------------------------------------
    useEffect(() => {
        if (!spawnLoading || loading) return undefined;
        // Exit spawn-loading when tasks are present (either returned as items, or
        // detected as all-done via group.progress by the hook).
        if (totalCount > 0 || allDoneCount > 0) {
            setSpawnLoading(false);
            return undefined;
        }
        if (spawnRetryRef.current >= SPAWN_MAX_RETRIES) {
            setSpawnGaveUp(true);
            // If preview items are available, exit spawn-loading so the body renders
            // via the isPreview path instead of showing the V1 FollowUp fallback.
            if (templatePreviewItems.length > 0) {
                setSpawnLoading(false);
            }
            return undefined;
        }
        spawnRetryRef.current += 1;
        const t = setTimeout(() => refresh(), SPAWN_RETRY_DELAY_MS);
        return () => clearTimeout(t);
    }, [spawnLoading, loading, totalCount, allDoneCount, templatePreviewItems.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---------------------------------------------------------------------------
    // Reconcile featuredOverride against latest hook data.
    // Runs whenever `groups` updates (i.e. after every refresh).  If the override
    // now points at a completed or absent item, advance to the next active one or
    // clear it.  The dep array does NOT include featuredOverride — only `groups` —
    // so this effect can never trigger itself and cause an infinite loop.
    // ---------------------------------------------------------------------------
    useEffect(() => {
        // Purge optimistic IDs once the server confirms them — either as done/skipped,
        // OR when they reappear in the active groups (i.e. the item was reopened/undone
        // server-side).  Without the second check the id would stay in the set and
        // permanently hide a reopened item until unmount — which breaks Undo (Task #15).
        setOptimisticDoneIds(prev => {
            if (prev.size === 0) return prev;
            const serverDoneIds = new Set(done.map(i => i.id));
            const serverActiveIds = new Set([...rawToday, ...rawLater].map(i => i.id));
            const stillPending = [...prev].filter(
                id => !serverDoneIds.has(id) && !serverActiveIds.has(id)
            );
            if (stillPending.length === prev.size) return prev; // no change — avoid re-render
            return new Set(stillPending);
        });
        // Reconcile featuredOverride against the refreshed groups.
        if (!featuredOverride) return;
        const reconcilePool = [...(today || []), ...(later || [])].filter((i) => !i.completed_at);
        const stillActive = reconcilePool.find((i) => i.id === featuredOverride.id);
        if (!stillActive) {
            // Override became stale (item completed / absent after refresh) — advance.
            setFeaturedOverride(reconcilePool[0] || null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups]); // intentionally excludes featuredOverride + optimisticDoneIds to prevent loops

    // ---------------------------------------------------------------------------
    // Early returns (all hooks above this line)
    // ---------------------------------------------------------------------------

    if (!hasFeatureAccess('tasks-v2', practiceId)) return null;

    if (initialLoading && !spawnLoading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} sx={{ color: '#078DEE' }} />
            </Box>
        );
    }

    if (templatesEmpty) return null;

    // ---------------------------------------------------------------------------
    // Gate (when rendered internally)
    // ---------------------------------------------------------------------------
    const isLeadBooked = (leadDetail?.lead_treatments || []).some(
        (t) => t.status === BOOKED_STATUS
    );

    if (!gateRenderedInHost && mode === 'gate-first' && isLeadBooked && !gateDismissed) {
        const handleGateDismissed = () => {
            setGateDismissed(true);
            const ltId =
                (leadDetail?.lead_treatments || []).find(
                    (t) => t.status === 'CONSULTATION_BOOKED'
                )?.id || leadDetail?.lead_treatments?.[0]?.id;
            if (ltId) startUndo(ltId);
        };
        return (
            <AttendanceGate
                variant="card"
                leadDetail={leadDetail}
                handleNoAttended={handleNoAttended}
                onDismissed={handleGateDismissed}
            />
        );
    }

    // ---------------------------------------------------------------------------
    // Spawn-loading state — same as v1
    // ---------------------------------------------------------------------------
    if (spawnLoading) {
        // All retries exhausted + no tasks spawned → fall back to V1 FollowUp.
        // Only fall back to V1 FollowUp when there are no preview items either —
        // if a template applies, show the preview body instead.
        if (spawnGaveUp && totalCount === 0 && templatePreviewItems.length === 0) {
            return (
                <SpawnFailureFallback
                    handleClose={handleClose}
                    onTaskActivity={onTaskActivity}
                    practiceId={practiceId}
                    leadId={leadId}
                    leadDetail={leadDetail}
                    onOpenSidebarTab={onOpenSidebarTab}
                    handleReloadLeads={handleReloadLeads}
                />
            );
        }
        const handleManualRetry = () => {
            setSpawnGaveUp(false);
            spawnRetryRef.current = 0;
            refresh();
        };
        const spawnMessage = spawnRetryRef.current >= 1
            ? 'Tasks are taking longer than usual…'
            : 'Spawning your tasks…';
        return (
            <Box
                data-testid="spawn-loading"
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3, px: 1 }}
            >
                {!spawnGaveUp && <CircularProgress size={18} sx={{ color: '#078DEE', flexShrink: 0 }} />}
                <Typography sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em' }}>
                    {spawnMessage}
                </Typography>
                {spawnGaveUp && (
                    <Button
                        data-testid="spawn-retry-btn"
                        size="small"
                        onClick={handleManualRetry}
                        sx={{ fontSize: '12px', color: '#078DEE', textTransform: 'none', p: 0, minWidth: 0 }}
                    >
                        Retry
                    </Button>
                )}
            </Box>
        );
    }

    if (!showBody) return null;

    // ---------------------------------------------------------------------------
    // v2 entrance helper — stats strip uses slide-down (vs v1's slide-up)
    // ---------------------------------------------------------------------------
    const statsEntranceSx = reducedMotion
        ? {}
        : {
            opacity: bodyMounted ? 1 : 0,
            transform: bodyMounted ? 'none' : 'translateY(-12px)',
            transition: `opacity 400ms ${EASING} 0ms, transform 400ms ${EASING} 0ms`,
        };

    // Stagger helper for non-stats sections (same slide-up as v1 but slightly shorter)
    const fadeSx = (delayMs) =>
        reducedMotion
            ? {}
            : {
                opacity: bodyMounted ? 1 : 0,
                transform: bodyMounted ? 'none' : 'translateY(16px)',
                transition: `opacity 400ms ${EASING} ${delayMs}ms, transform 400ms ${EASING} ${delayMs}ms`,
            };

    // ---------------------------------------------------------------------------
    // All-done — celebratory card (same as v1)
    // ---------------------------------------------------------------------------
    if (allDone) {
        return (
            <Box className="gchk-root" data-testid="attended-guided-checklist-v2" data-mode={mode} sx={{ py: 2, width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden', boxSizing: 'border-box' }}>
                <Box
                    data-testid="all-done-card"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 4,
                        px: 3,
                        bgcolor: 'rgba(52, 199, 89, 0.06)',
                        borderRadius: '16px',
                        textAlign: 'center',
                    }}
                >
                    <Box
                        sx={{
                            width: 52,
                            height: 52,
                            borderRadius: '50%',
                            bgcolor: 'rgba(52, 199, 89, 0.12)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 1.5,
                        }}
                    >
                        <Iconify icon="mdi:check-circle" width={28} height={28} sx={{ color: '#34c759' }} />
                    </Box>
                    <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.022em', mb: 0.5 }}>
                        Nothing else to do
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#6e6e73', letterSpacing: '-0.01em', mb: 2.5 }}>
                        All {effectiveTotalCount} tasks are complete.
                    </Typography>
                    {handleClose && (
                        <Button
                            data-testid="all-done-close-btn"
                            variant="contained"
                            disableElevation
                            onClick={handleClose}
                            sx={{
                                bgcolor: '#34c759',
                                color: '#ffffff',
                                fontSize: '14px',
                                fontWeight: 600,
                                letterSpacing: '-0.01em',
                                textTransform: 'none',
                                borderRadius: '6px',
                                px: 3,
                                py: 0.875,
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#2db14e', boxShadow: 'none' },
                            }}
                        >
                            Close
                        </Button>
                    )}
                </Box>
            </Box>
        );
    }

    // ---------------------------------------------------------------------------
    // Empty items (no live tasks, no preview templates)
    // ---------------------------------------------------------------------------
    if (totalCount === 0 && templatePreviewItems.length === 0) {
        return (
            <Box className="gchk-root" data-testid="attended-guided-checklist-v2" data-mode={mode} sx={{ py: 2, width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden', boxSizing: 'border-box' }}>
                <Box sx={{ p: 3, bgcolor: '#F5F5F7', borderRadius: '12px', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#6e6e73', letterSpacing: '-0.01em' }}>
                        No tasks yet — they'll appear here when ready.
                    </Typography>
                </Box>
            </Box>
        );
    }

    // ---------------------------------------------------------------------------
    // Due-date override helpers — defined before effectiveUpNext usage
    // ---------------------------------------------------------------------------

    const applyDueDateOverride = (item) => {
        if (!item || !dueDateOverrides.has(item.taskId)) return item;
        return { ...item, due_at: dueDateOverrides.get(item.taskId).toISOString() };
    };

    const handleChangeDueDate = async (item, newDate) => {
        // Preview items have no taskId — spawn from template first to get a real taskId.
        if (!item?.taskId) {
            if (!item?._templateId) {
                dispatch(updateError({ success: false, message: "Due dates can't be set on this item yet." }));
                return;
            }
            const spawnResp = await callApi(
                `/api/v1/practices/${practiceId}/tasks/from-template/`,
                'post',
                { template_id: item._templateId, lead_id: leadId }
            );
            if (!spawnResp.ok && spawnResp.status !== 200 && spawnResp.status !== 201) {
                dispatch(updateError({ success: false, message: "Couldn't create tasks. Please try again." }));
                return;
            }
            const newTaskId = spawnResp.data?.id;
            if (newTaskId) {
                await callApi(`/api/v1/practices/${practiceId}/tasks/${newTaskId}/`, 'patch', { due_at: newDate.toISOString() });
                dispatch(updateError({ success: true, message: 'Tasks created and due date set.' }));
                // Setting a due date IS a task action — let the host enable Finish.
                if (onTaskActivity) onTaskActivity();
            } else {
                dispatch(updateError({ success: true, message: 'Tasks created! Select a task to set its due date.' }));
            }
            refresh();
            return;
        }
        const allItems = [...today, ...later, ...done];
        const cascade = cascadeTaskDueDates({
            items: allItems,
            anchorTaskId: item.taskId,
            oldDueAt: item.due_at,
            newDueAt: newDate,
        });

        const prevOverrides = new Map(dueDateOverrides);
        const nextOverrides = new Map(dueDateOverrides);
        cascade.forEach(({ taskId, newDueAt: d }) => nextOverrides.set(taskId, d));
        setDueDateOverrides(nextOverrides);

        const results = await Promise.allSettled(
            cascade.map(({ taskId, newDueAt: d }) =>
                callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/`, 'patch', {
                    due_at: d.toISOString(),
                    ...(taskId === item.taskId ? { is_anchor: true } : {}),
                })
            )
        );

        const anyFailed = results.some((r) => r.status === 'rejected' || !r.value?.ok);
        if (anyFailed) {
            setDueDateOverrides(prevOverrides);
            dispatch(updateError({ success: false, message: "Couldn't update due dates. Please try again." }));
            return;
        }

        const nShifted = cascade.length;
        const oldMs = item.due_at ? new Date(item.due_at).getTime() : Date.now();
        const deltaDays = Math.round(Math.abs(newDate.getTime() - oldMs) / (24 * 60 * 60 * 1000));
        dispatch(updateError({
            success: true,
            message: `Due dates updated — ${nShifted} task${nShifted !== 1 ? 's' : ''} shifted by ${deltaDays} day${deltaDays !== 1 ? 's' : ''}`,
        }));
        // Rescheduling a task is a completed action for this visit (the user isn't
        // ready to do more now) — fire IMMEDIATELY so the host sets the attended
        // outcome and the Finish button activates, same as complete/skip/pause.
        if (onTaskActivity) onTaskActivity();
        refresh();
    };

    // ---------------------------------------------------------------------------
    // Active data — when no live tasks exist but templates do, treat preview items
    // identically to real spawned tasks (no preview-banner, fully interactive).
    // ---------------------------------------------------------------------------
    const isPreview = totalCount === 0 && templatePreviewItems.length > 0;
    const activeToday  = isPreview ? templatePreviewItems : today;
    const activeLater  = isPreview ? [] : later;
    const activeDone   = isPreview ? [] : done;
    const activeDoneCount  = activeDone.length;
    const activeTotalCount = activeToday.length + activeLater.length + activeDoneCount;

    const effectiveUpNext = applyDueDateOverride(
        featuredOverride || (isPreview ? (templatePreviewItems[0] || null) : upNext)
    );
    const activePool = [...activeToday, ...activeLater].filter((i) => !i.completed_at);
    const currentIdx = effectiveUpNext?.id != null
        ? activePool.findIndex((i) => i.id === effectiveUpNext.id)
        : 0;
    // When currentIdx is -1 (featured item was completed and fell out of activePool),
    // fall back to the first two remaining items so the right pane can still show something.
    const nextUpNextRaw = currentIdx >= 0
        ? (activePool[currentIdx + 1] || null)
        : (activePool[1] || activePool[0] || null);
    const nextUpNext = nextUpNextRaw ? applyDueDateOverride(nextUpNextRaw) : null;
    // Signal "All caught up" placeholder when current item is last in pool.
    const isLastInPool = currentIdx >= 0 && currentIdx === activePool.length - 1;
    const nextEmptyReason = (!nextUpNext && isLastInPool) ? 'caught_up' : null;

    const overriddenActiveGroups = {
        today: activeToday.map(applyDueDateOverride),
        later: activeLater.map(applyDueDateOverride),
        done:  activeDone.map(applyDueDateOverride),
    };
    const hasOverdue = activeToday.some((item) => item.due_at && new Date(item.due_at) < new Date());
    const todayChipColor = hasOverdue ? '#FF5630' : '#FFAB00';
    // All non-done active tasks — used for bulk-assign.
    const activeTasks = [...activeToday, ...activeLater].filter((t) => t.taskId);

    // ---------------------------------------------------------------------------
    // Handlers
    // ---------------------------------------------------------------------------
    const handleItemClick = (item) => {
        if (item.completed_at) return;
        setFeaturedOverride(item);
    };

    const handleSkip = (skippedItem) => {
        const pool = isPreview ? templatePreviewItems : [...today, ...later];
        const remaining = pool.filter(
            (i) => !i.completed_at && i.id !== skippedItem.id
        );
        // Preview items have no real taskId/itemId — skip client-side only.
        if (!skippedItem?.taskId || !skippedItem?.itemId) {
            setFeaturedOverride(remaining[0] || null);
            return;
        }
        // Persist the skip to the BE so the item stays de-prioritised after reload.
        // Uses the same /complete/ endpoint as a full completion, with SKIPPED state.
        // Optimistic UI advance is gated on a confirmed 200 — same pattern as handlePanelComplete.
        dispatch(completeChecklistItem(
            practiceId, skippedItem.taskId, skippedItem.itemId,
            ({ success }) => {
                if (success) {
                    setOptimisticDoneIds(prev => { const s = new Set(prev); s.add(skippedItem.id); return s; });
                    setFeaturedOverride(remaining[0] || null);
                    if (onTaskActivity) onTaskActivity();
                    // Cache-bust the GET so the refetch returns real server state.
                    refresh({ bustCache: true });
                }
                // On failure the thunk already surfaced an error toast; leave the card as-is.
            },
            { source: 'upnext_skip' },
            CHECKLIST_COMPLETION_STATE.SKIPPED,
        ));
    };

    const handleActionClick = (item) => {
        if (!item) return;
        if (INLINE_HANDLED_TYPES.has(item?.item_type)) return;
        setActionPanelItem(item);
    };

    const handlePanelClose = () => setActionPanelItem(null);

    // messageId is forwarded from EmailActionBody.handleSendSuccess (IosEmailComposer)
    // so we can store it in response_payload for delivery-status polling.
    const handlePanelSendSuccess = (messageId) => {
        const sentItem = actionPanelItem;
        setActionPanelItem(null);
        if (!sentItem) { refresh({ bustCache: true }); return; }

        // Preview items have no real taskId — advance immediately without a completion call.
        if (!sentItem.taskId || !sentItem.itemId) {
            const pool = templatePreviewItems;
            const remaining = pool.filter((i) => !i.completed_at && i.id !== sentItem.id);
            setFeaturedOverride(remaining[0] || null);
            const label = COMPLETION_LABELS[sentItem.item_type] || DEFAULT_COMPLETION_LABEL;
            if (recentlyCompletedTimerRef.current) clearTimeout(recentlyCompletedTimerRef.current);
            setRecentlyCompleted({ completed: sentItem, label, completedAt: new Date(), fromSend: true });
            recentlyCompletedTimerRef.current = setTimeout(() => {
                setRecentlyCompleted(null);
                if (onTaskActivity) onTaskActivity();
            }, 4000);
            refresh({ bustCache: true });
            return;
        }

        // Explicitly complete the checklist item — makes the email-sent→Done transition
        // reliable, shows the green Done chip, and posts to Timeline (#16 / #20).
        // response_payload mirrors ComposerCell's markComplete shape: { channel, messageId }.
        // Gate optimistic-done + PostSendExpandedCard on the confirmed 200 — same pattern
        // as handlePanelComplete — so a BE error can't show a false "Done" confirmation.
        const SEND_CHANNEL = { WHATSAPP_SENT: 'whatsapp', SMS_SENT: 'sms' };
        const responsePayload = {
            channel: SEND_CHANNEL[sentItem.item_type] || 'email',
            messageId: messageId || null,
        };
        dispatch(completeChecklistItem(
            practiceId, sentItem.taskId, sentItem.itemId,
            ({ success }) => {
                if (success) {
                    // Mark optimistically done so the mini-grid can't flicker back to active.
                    setOptimisticDoneIds(prev => { const s = new Set(prev); s.add(sentItem.id); return s; });
                    const pool = isPreview ? templatePreviewItems : [...today, ...later];
                    const remaining = pool.filter((i) => !i.completed_at && i.id !== sentItem.id);
                    setFeaturedOverride(remaining[0] || null);
                    const label = COMPLETION_LABELS[sentItem.item_type] || DEFAULT_COMPLETION_LABEL;
                    if (recentlyCompletedTimerRef.current) clearTimeout(recentlyCompletedTimerRef.current);
                    // fromSend=true triggers PostSendExpandedCard on the right (Task #6).
                    setRecentlyCompleted({ completed: sentItem, label, completedAt: new Date(), fromSend: true });
                    // Fire onTaskActivity IMMEDIATELY (not deferred) so the host selects
                    // the 'Reminder & Follow-up' outcome and the Finish button enables.
                    // Setting activeTab='Reminder & Follow-up' keeps the checklist mounted
                    // (gate: !activeTab || activeTab === 'Reminder & Follow-up'), so this
                    // does NOT unmount the inline V2 checklist. The timeout below only
                    // clears the transient confirmation card. (mirrors handleSkip)
                    if (onTaskActivity) onTaskActivity();
                    // Clear the PostSendExpandedCard after its display window (4s).
                    recentlyCompletedTimerRef.current = setTimeout(() => {
                        setRecentlyCompleted(null);
                    }, 4000);
                }
                // Cache-bust the GET so the refetch returns real server state.
                refresh({ bustCache: true });
            },
            responsePayload,
            CHECKLIST_COMPLETION_STATE.YES,
        ));
    };

    const handlePanelComplete = (completedItem) => {
        setActionPanelItem(null);
        if (!completedItem?.taskId || !completedItem?.itemId) { refresh({ bustCache: true }); return; }
        // For LEAD_NEXT_ACTION the BE requires resolved_action in the body.
        // NextActionPanel attaches resolvedAction to the completedItem before
        // calling onComplete — forward it here so the POST includes the field.
        const payload = completedItem.resolvedAction
            ? { resolved_action: completedItem.resolvedAction }
            : null;
        dispatch(completeChecklistItem(
            practiceId, completedItem.taskId, completedItem.itemId,
            ({ success }) => {
                // Gate the advance + confirmation card behind a confirmed 200 from the BE.
                // Previously setRecentlyCompleted fired optimistically before the thunk,
                // so a 400/500 response still showed "Email sent" then silently reverted.
                if (success) {
                    // Mark optimistically done so the mini-grid can't flicker back to active
                    // while the busted-cache GET is in-flight.
                    setOptimisticDoneIds(prev => { const s = new Set(prev); s.add(completedItem.id); return s; });
                    // Advance the featured card immediately — single source of truth.
                    // Mirror handleSkip: first remaining non-completed item, excluding the just-completed one.
                    const pool = isPreview ? templatePreviewItems : [...today, ...later];
                    const remaining = pool.filter((i) => !i.completed_at && i.id !== completedItem.id);
                    setFeaturedOverride(remaining[0] || null);
                    // Decorative ✓ ack — not the navigation source of truth.
                    const label = COMPLETION_LABELS[completedItem.item_type] || DEFAULT_COMPLETION_LABEL;
                    if (recentlyCompletedTimerRef.current) clearTimeout(recentlyCompletedTimerRef.current);
                    setRecentlyCompleted({ completed: completedItem, label, completedAt: new Date() });
                    // Fire onTaskActivity IMMEDIATELY so the host selects the
                    // 'Reminder & Follow-up' outcome and the Finish button enables.
                    // The deferred call inside setTimeout could be cancelled by a
                    // re-mount before it ran, leaving Finish stuck disabled. Setting
                    // activeTab='Reminder & Follow-up' satisfies the render gate, so
                    // the checklist stays mounted. (mirrors handleSkip)
                    if (onTaskActivity) onTaskActivity();
                    // The timeout now only clears the transient ✓ ack card.
                    recentlyCompletedTimerRef.current = setTimeout(() => {
                        setRecentlyCompleted(null);
                    }, 4000);
                }
                // Cache-bust the GET so the refetch returns real server state, not the 32s-cached response.
                refresh({ bustCache: true });
            },
            payload,
        ));
    };

    // ---------------------------------------------------------------------------
    // Pause handler — opens dialog pre-filled to 7 days from now (local time)
    // ---------------------------------------------------------------------------
    const handlePause = (item) => {
        const d = new Date();
        d.setDate(d.getDate() + 7);
        const pad = (n) => String(n).padStart(2, '0');
        const localStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`;
        setPauseUntilDate(localStr);
        setPauseDialogItem(item);
    };

    const handlePauseConfirm = async () => {
        const item = pauseDialogItem;
        if (!item?.taskId || !item?.itemId || !pauseUntilDate) return;
        setPauseDialogItem(null);
        const resp = await callApi(
            `/api/v1/practices/${practiceId}/tasks/${item.taskId}/checklist/${item.itemId}/pause/`,
            'post',
            { paused_until: new Date(pauseUntilDate).toISOString() }
        );
        if (resp.ok) {
            setOptimisticDoneIds(prev => { const s = new Set(prev); s.add(item.id); return s; });
            const pool = isPreview ? templatePreviewItems : [...today, ...later];
            const remaining = pool.filter((i) => !i.completed_at && i.id !== item.id);
            setFeaturedOverride(remaining[0] || null);
            dispatch(updateError({ success: true, message: 'Task paused' }));
            // Fire onTaskActivity IMMEDIATELY (not deferred) so the host selects the
            // "Reminder & Follow-up" outcome and the Finish button enables right away.
            // Setting activeTab='Reminder & Follow-up' satisfies the render gate, so the
            // checklist does NOT unmount — same fix as handlePanelComplete/SendSuccess.
            if (onTaskActivity) onTaskActivity();
            refresh({ bustCache: true });
        } else {
            dispatch(updateError({ success: false, message: resp?.data?.detail || 'Failed to pause task' }));
        }
    };

    // ---------------------------------------------------------------------------
    // Undo handler — reopens a done/skipped/paused checklist item
    // ---------------------------------------------------------------------------
    const handleUndo = (item) => {
        if (!item?.taskId || !item?.itemId) return;
        dispatch(reopenChecklistItem(
            practiceId, item.taskId, item.itemId,
            ({ success }) => { if (success) refresh({ bustCache: true }); }
        ));
    };

    // ---------------------------------------------------------------------------
    // Guided checklist body — v2 sub-components, stats strip slides DOWN
    // ---------------------------------------------------------------------------
    return (
        <Box className="gchk-root" data-testid="attended-guided-checklist-v2" data-mode={mode} sx={{ py: 2, width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden', boxSizing: 'border-box' }}>
            {/* Undo-attendance banner (gate-first only, 30s window) */}
            {showBanner && mode === 'gate-first' && (
                <UndoBanner
                    secondsLeft={secondsLeft}
                    onUndo={undo}
                    onDismiss={dismissUndo}
                />
            )}
            {/* Stats strip — full width, above split row so it never compresses when
                 the action panel opens beside the UpNext card.  */}
            <Box
                className="gchk-stats-strip"
                data-testid="stats-strip"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: 28,
                    mb: 2,
                    ...statsEntranceSx,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    {activeToday.length > 0 && (
                        <Box data-testid="stats-chip-today" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: todayChipColor, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#3c3c43', letterSpacing: '-0.005em' }}>
                                {activeToday.length} Due today
                            </Typography>
                        </Box>
                    )}
                    {activeLater.length > 0 && (
                        <Box data-testid="stats-chip-later" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6e6e73', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#3c3c43', letterSpacing: '-0.005em' }}>
                                {activeLater.length} Later
                            </Typography>
                        </Box>
                    )}
                    {activeDoneCount > 0 && (
                        <Box data-testid="stats-chip-done" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#34c759', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#3c3c43', letterSpacing: '-0.005em' }}>
                                {activeDoneCount} Done
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Assign all button — visible when there are active tasks */}
                {activeTasks.length > 0 && (
                    <Button
                        data-testid="bulk-assign-btn"
                        size="small"
                        variant="outlined"
                        startIcon={<Iconify icon="mdi:account-multiple-plus-outline" width={14} height={14} />}
                        onClick={() => setBulkAssignOpen(true)}
                        sx={{
                            textTransform: 'none',
                            fontSize: '12px',
                            fontWeight: 600,
                            borderRadius: '8px',
                            color: '#078DEE',
                            borderColor: '#078DEE44',
                            py: 0.5,
                            px: 1.25,
                            '&:hover': { bgcolor: '#078DEE0A', borderColor: '#078DEE' },
                        }}
                    >
                        Assign all
                    </Button>
                )}

                {/* Progress bar — flex:1 so it fills the remaining strip width */}
                <Box sx={{ flex: 1, maxWidth: 320, ml: 1.5, height: 4, borderRadius: '2px', bgcolor: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                    <Box
                        data-testid="progress-bar-fill"
                        sx={{
                            height: '100%',
                            width: `${activeTotalCount > 0 ? (activeDoneCount / activeTotalCount) * 100 : 0}%`,
                            bgcolor: '#34c759',
                            borderRadius: '2px',
                            transition: 'width 400ms ease',
                        }}
                    />
                </Box>
            </Box>

            {/* Split row: UpNext left + action panel right.
                 Only UpNext lives here so the action panel doesn't compress stats/mini-grid. */}
            <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2 }}>
                {/* Left column — UpNext card only */}
                <Box
                    sx={{
                        flex: actionPanelItem ? '1 1 0' : '1',
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        transition: `flex 320ms ${EASING}`,
                    }}
                >
                    {/* Up Next card — v2 variant, or post-action 30/70 swap */}
                    <Box className="gchk-upnext-wrap" sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', ...fadeSx(80) }}>
                        {recentlyCompleted ? (
                            <Box
                                data-testid="upnext-swap-layout"
                                sx={{ display: 'flex', alignItems: 'stretch', gap: 2 }}
                            >
                                <Box sx={{ flex: '0 0 calc(30% - 8px)', minWidth: 0 }}>
                                    <RecentlyCompletedCard
                                        item={recentlyCompleted.completed}
                                        label={recentlyCompleted.label}
                                        completedAt={recentlyCompleted.completedAt}
                                        onDismiss={() => setRecentlyCompleted(null)}
                                    />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {/* featuredOverride is the single source of truth — effectiveUpNext
                                        already reflects the post-completion advance, no snapshot needed.
                                        fromSend → expanded PostSendExpandedCard with editable due date;
                                        otherwise normal UpNextCardV2. */}
                                    {recentlyCompleted.fromSend ? (
                                        <PostSendExpandedCard
                                            key={effectiveUpNext?.id ?? 'post-send'}
                                            item={effectiveUpNext}
                                            onChangeDueDate={handleChangeDueDate}
                                            practiceId={practiceId}
                                        />
                                    ) : (
                                        <UpNextCardV2
                                            key={effectiveUpNext?.id ?? 'incoming'}
                                            item={effectiveUpNext}
                                            leadEmail={leadDetail?.email || leadDetail?.lead_contact?.email || ''}
                                            onActionClick={handleActionClick}
                                            onSkip={handleSkip}
                                            onPause={handlePause}
                                            onChangeDueDate={handleChangeDueDate}
                                            nextItem={null}
                                            nextEmptyReason={null}
                                            onItemClick={handleItemClick}
                                            practiceId={practiceId}
                                        />
                                    )}
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                                <UpNextCardV2
                                    key={effectiveUpNext?.id ?? 'none'}
                                    item={effectiveUpNext}
                                    leadEmail={leadDetail?.email || leadDetail?.lead_contact?.email || ''}
                                    onActionClick={handleActionClick}
                                    onSkip={handleSkip}
                                    onPause={handlePause}
                                    onChangeDueDate={handleChangeDueDate}
                                    nextItem={nextUpNext}
                                    nextEmptyReason={nextEmptyReason}
                                    onItemClick={handleItemClick}
                                    practiceId={practiceId}
                                    panelOpen={!!actionPanelItem}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Right column — action panel */}
                {actionPanelItem && (
                    <ActionPanel
                        item={actionPanelItem}
                        practiceId={practiceId}
                        leadId={leadId}
                        leadDetail={leadDetail}
                        onClose={handlePanelClose}
                        onSendSuccess={handlePanelSendSuccess}
                        onComplete={handlePanelComplete}
                        handleReloadLeads={handleReloadLeads}
                        onOpenSidebarTab={onOpenSidebarTab}
                    />
                )}
            </Box>

            {/* Mini-grid — full width, below split row so it never compresses when
                 the action panel is open beside the UpNext card. */}
            <Box className="gchk-list-wrap" sx={{ mt: 3, ...fadeSx(160) }}>
                <ChecklistMiniGrid
                    groups={overriddenActiveGroups}
                    onItemClick={handleItemClick}
                    onUndo={handleUndo}
                />
            </Box>

            {/* Bulk assign modal */}
            <BulkAssignModal
                open={bulkAssignOpen}
                onClose={() => setBulkAssignOpen(false)}
                practiceId={practiceId}
                activeTasks={activeTasks}
                onSaved={() => { setBulkAssignOpen(false); refresh(); }}
            />

            {/* Pause dialog — date picker for paused_until */}
            <Dialog
                data-testid="pause-dialog-v2"
                open={pauseDialogItem != null}
                onClose={() => setPauseDialogItem(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6e6e73', mb: 0.5 }}>
                        Pause task
                    </Typography>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                        {pauseDialogItem?.title || 'Pause task'}
                    </Typography>
                </Box>
                <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
                    <Typography sx={{ fontSize: '13px', color: '#3c3c43', mb: 1.5, letterSpacing: '-0.005em' }}>
                        Resume this task on:
                    </Typography>
                    <input
                        type="datetime-local"
                        value={pauseUntilDate}
                        onChange={(e) => setPauseUntilDate(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '10px 12px',
                            fontSize: '14px',
                            borderRadius: '8px',
                            border: '1.5px solid rgba(0,0,0,0.15)',
                            fontFamily: 'inherit',
                            color: '#1d1d1f',
                            outline: 'none',
                            boxSizing: 'border-box',
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.75, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                    <Button
                        onClick={() => setPauseDialogItem(null)}
                        sx={{ color: '#6e6e73', textTransform: 'none', fontSize: '14px', fontWeight: 500 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        data-testid="pause-confirm-btn-v2"
                        variant="contained"
                        disableElevation
                        disabled={!pauseUntilDate}
                        onClick={handlePauseConfirm}
                        sx={{ ...CRM_PRIMARY_BUTTON_SX, textTransform: 'none', fontSize: '14px', fontWeight: 600, py: 0.75, px: 2.5, borderRadius: '8px' }}
                    >
                        Pause task
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

AttendedGuidedChecklistV2.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadDetail: PropTypes.object,
    mode: PropTypes.oneOf(['gate-first', 'post-attended']).isRequired,
    handleClose: PropTypes.func,
    handleNoAttended: PropTypes.func,
    gateRenderedInHost: PropTypes.bool,
    externalGateDismissed: PropTypes.bool,
    /** Deferred table reload — called ~3s after archive dispatched so Celery drains first. */
    handleReloadLeads: PropTypes.func,
    /** Fires after a task is completed, sent, or skipped server-side. Host pops Finish open. */
    onTaskActivity: PropTypes.func,
    /** Called with 'email' or 'whatsapp' to open the matching right-sidebar tab in the host popup. */
    onOpenSidebarTab: PropTypes.func,
};

AttendedGuidedChecklistV2.defaultProps = {
    leadDetail: null,
    handleClose: null,
    handleNoAttended: null,
    gateRenderedInHost: false,
    externalGateDismissed: false,
    onTaskActivity: null,
    onOpenSidebarTab: null,
};
